import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import api from 'api';
import { EnterpriseMember, UpdateEnterpriseMember } from 'models/Enterprises';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import validations from 'validations';
import InputField from 'components/InputField';
import useValidations from 'router/subrouters/Auth/hooks/useValidations';
import PhoneFieldWrapper from 'components/PhoneFieldWrapper';
import DropzoneField from 'components/DropzoneField';
import { Button, Loader } from 'ncoded-component-library';
import useCallbackRef from 'hooks/useCallbackRef';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { DropzoneFile } from 'components/Dropzone/utils';
import { DropzoneUpload } from 'models/Dropzone';
import EmailField from 'components/EmailField';
import utils from 'utils';

import './EditMemberDrawer.styles.scss';

type EditMemberDrawerProps = {
  className?: string;
  member?: Partial<EnterpriseMember>;
  updateImage: (profileImage: DropzoneFile) => void;
  onSuccess: (member: Partial<UpdateEnterpriseMember>) => void;
};

const EditMemberDrawer: ForwardRefRenderFunction<
  SideDrawerRef,
  EditMemberDrawerProps
> = (props, ref) => {
  const { className, member, onSuccess, updateImage } = props;

  const { _id } = member || {};

  const classes = classNames('edit-member-drawer', className);

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { t } = useTranslation();
  const commonValidations = useValidations();

  const initialValues = useMemo(
    () => ({
      ...member,
      phone: member?.user?.phone,
      linkedinAccount: member?.user?.linkedinAccount,
    }),
    [member],
  );

  const handleOnSubmit = useCallback(
    async (values: UpdateEnterpriseMember) => {
      const { firstName, lastName, title, phone, linkedinAccount } = values;

      try {
        onSuccess?.({ firstName, lastName, title, phone, linkedinAccount });
        drawer?.setOpen(false);
      } catch (e) {
        return;
      }
    },
    [drawer, onSuccess],
  );

  const uploadProfileImage: DropzoneUpload = useCallback(
    async (file, fileName, config) => {
      const data = await api.enterprises.uploadProfileImage(
        file,
        fileName,
        _id,
        config,
      );
      updateImage(data);
      return data;
    },
    [_id, updateImage],
  );

  return (
    <SideDrawer ref={drawerRef} className={classes}>
      {member ? (
        <Form
          keepDirtyOnReinitialize
          initialValues={initialValues}
          onSubmit={handleOnSubmit}
          render={({ handleSubmit, submitting, invalid }) => {
            return (
              <form onSubmit={handleSubmit}>
                <p>{t('editYourProfile')}</p>

                <div>
                  <Field
                    name="firstName"
                    label={t('firstName')}
                    component={InputField}
                    required
                    validate={commonValidations.name}
                  />
                  <Field
                    name="lastName"
                    label={t('lastName')}
                    component={InputField}
                    required
                    validate={commonValidations.name}
                  />
                </div>

                <EmailField disabled required />

                <PhoneFieldWrapper />

                <Field
                  name="title"
                  label={t('professionalTitle')}
                  component={InputField}
                  required
                  validate={commonValidations.name}
                />

                <Field
                  name="linkedinAccount"
                  label={t('linkedin')}
                  component={InputField}
                  validate={validations.linkedinValidation()}
                />

                <Field
                  accept="image/*"
                  replaceFile
                  maxFiles={1}
                  label={t('profileImage')}
                  name="profileImage"
                  description={t('weAcceptPngJpegJpgWebp')}
                  component={DropzoneField}
                  uploadRequest={uploadProfileImage}
                  deleteRequest={utils.noop}
                />

                <Button type="submit" disabled={submitting || invalid}>
                  {t('save')}
                </Button>
              </form>
            );
          }}
        />
      ) : (
        <Loader />
      )}
    </SideDrawer>
  );
};

export default forwardRef(EditMemberDrawer);
